import {
	IconBug,
	IconCheck,
	IconClipboard,
	IconMailForward,
	IconRefresh,
	IconTerminal2,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Can I use ineedatest.email for automated CI/CD email testing?",
		answer:
			"No — ineedatest.email has no API or webhooks. It is a browser tool. For automated testing where your test suite needs to programmatically check an inbox and assert on email content, use a purpose-built tool like Mailosaur or Mailtrap. ineedatest.email is the right fit for manual verification flows: you spin up an address, trigger the email from your app, and check the inbox in your browser.",
	},
	{
		question: "How do I create a fresh inbox for each test scenario?",
		answer:
			"Open ineedatest.email, type the local part you want (e.g. signup-test-1), pick a domain, set an expiry that covers your test session, and copy the address. For each new test scenario, use a different local part. Because the inbox is URL-based and not session-based, you can have multiple tabs open with different inboxes simultaneously.",
	},
	{
		question:
			"Will ineedatest.email addresses get through my staging environment's email restrictions?",
		answer:
			"ineedatest.email accepts mail sent to any address at its receiving domains. As long as your staging environment's email sender can route to external SMTP, the email will arrive. If your staging environment only sends to an allowlisted domain, you may need to add one of ineedatest.email's receiving domains to the allowlist.",
	},
	{
		question: "How is this different from using Mailinator for email testing?",
		answer:
			"Mailinator's free tier uses shared public inboxes — anyone who knows the address can read the mail, which is a security issue for staging environments handling real OTP codes or password reset tokens. Private Mailinator inboxes require a paid plan starting at $99/month. ineedatest.email gives you private inboxes for free: the inbox URL is the credential, and there is no public directory. The tradeoff is that ineedatest.email has no API — Mailinator's paid tier does.",
	},
	{
		question: "Can I check HTML email rendering with ineedatest.email?",
		answer:
			"Yes. ineedatest.email stores both the text and HTML versions of received emails. You can view the rendered HTML directly in the inbox to verify your email template layout, check that links are correct, and confirm that transactional content looks right in a real rendered environment.",
	},
	{
		question: "What happens to OTP codes or tokens in emails I receive?",
		answer:
			"They appear in the email body as plain text or HTML and you can copy them directly. ineedatest.email does not parse or extract codes automatically — you read the email and copy the token manually. When the inbox expires, all messages are deleted.",
	},
];

const steps = [
	{
		icon: IconClipboard,
		step: "1",
		title: "Spin up an address for your test scenario",
		body: "Open ineedatest.email. Type a local part that identifies the test — something like signup-test or reset-flow. Pick a receiving domain, set an expiry that covers your test session, and copy the address.",
	},
	{
		icon: IconTerminal2,
		step: "2",
		title: "Configure your app to send to that address",
		body: "Paste the address wherever your test scenario needs it: a sign-up form, a test user record in your database, a direct API call to your notification service. No special configuration on ineedatest.email's side — it accepts mail at any address on its domains.",
	},
	{
		icon: IconMailForward,
		step: "3",
		title: "Trigger the email flow",
		body: "Run your test: submit the form, call the endpoint, or click through the flow as a real user would. Your transactional email service sends the message to the ineedatest.email address.",
	},
	{
		icon: IconCheck,
		step: "4",
		title: "Check the inbox and verify delivery",
		body: "Open the inbox URL. The received email appears with the full text and HTML body. Verify the content: subject line, sender address, OTP code, confirmation link, template rendering. Copy tokens if you need them for the next step in your flow.",
	},
	{
		icon: IconRefresh,
		step: "5",
		title: "Repeat with a fresh address per scenario",
		body: "Use a different local part for each test scenario. Because inboxes are URL-based, you can have several open in parallel. When the expiry runs out — or when you are done — the inbox and its contents are gone.",
	},
];

const useCases = [
	{
		icon: IconBug,
		title: "Sign-up and email verification flows",
		body: "Verify that your app sends a confirmation email after registration, that the link in the email works, and that the from address and subject line are correct. Create one address per test user and check each inbox independently.",
	},
	{
		icon: IconMailForward,
		title: "Password reset and OTP delivery",
		body: "Confirm that your password reset emails arrive, that the token in the email is valid, and that the expiry on the token matches your spec. Check the raw HTML to verify you are not leaking any PII in the email body.",
	},
	{
		icon: IconTerminal2,
		title: "Transactional email template QA",
		body: "View the rendered HTML version of your email in a real inbox. Catch broken links, wrong merge fields, and layout issues before they reach customers. ineedatest.email stores the HTML body so you can inspect it directly.",
	},
	{
		icon: IconCheck,
		title: "Staging and pre-production smoke tests",
		body: "Use a fresh ineedatest.email address as the test user in your staging environment. Verify the full end-to-end email delivery path — from your application through your transactional email provider and into a real inbox — without touching real user data.",
	},
];

export const Route = createFileRoute("/_marketing/for/testing")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Disposable email for testing — ineedatest.email",
			description:
				"Spin up a private throwaway inbox for each test scenario. Verify email delivery, OTP codes, and HTML rendering with no account required.",
			keywords:
				"disposable email for testing, temporary email for testing, throwaway email testing, email testing inbox, test email address, staging email testing",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Disposable email for testing</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							Email testing
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Disposable email addresses for testing
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							Spin up a private throwaway inbox for each test scenario. Verify
							your sign-up flows, OTP delivery, password resets, and HTML email
							templates against a real inbox — no account, no API keys, no cost.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Limitation callout */}
			<section className="border-t border-border/50 bg-muted/15 py-8">
				<MarketingContainer>
					<p className="max-w-2xl text-sm text-muted-foreground">
						<span className="font-semibold text-foreground">
							Not for automated CI/CD pipelines.
						</span>{" "}
						ineedatest.email is a browser tool with no API. It is built for
						manual verification — a developer checking an inbox in a browser
						tab. For programmatic inbox access in test suites, use Mailosaur or
						Mailtrap. For everything else, read on.
					</p>
				</MarketingContainer>
			</section>

			{/* Step by step */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="How it works"
						heading="Set up a test inbox in under 30 seconds"
						description="No account, no configuration. Spin up an address, point your app at it, check the inbox."
						className="mb-10 sm:mb-12"
					/>
					<div className="flex flex-col gap-6">
						{steps.map((step, index) => {
							const Icon = step.icon;
							return (
								<div
									key={step.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.08}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-1.5">
										<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
											Step {step.step}
										</p>
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{step.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{step.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Use cases */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Use cases"
						heading="What developers use it for"
						description="Common email testing scenarios where a private, expiring disposable inbox is exactly what you need."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{useCases.map((item, index) => {
							const Icon = item.icon;
							return (
								<div
									key={item.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{item.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{item.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Why not Mailinator */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="vs Mailinator"
						heading="Why not just use Mailinator for staging tests?"
						description="Mailinator is the default recommendation for email testing. Here is why it falls short for most individual developers."
						className="mb-8 sm:mb-10"
					/>
					<div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
						{[
							{
								title: "Free tier inboxes are public",
								body: "Mailinator's free inboxes are readable by anyone. In a staging environment where you are testing real OTP codes or password reset tokens, a public inbox is a security problem. You need a private inbox that only you can access.",
							},
							{
								title: "Private inboxes cost $99+/month",
								body: "Private Mailinator domains and API access start at $99/month. That is reasonable for an engineering team running automated CI tests, but significant overhead for a solo developer who needs to manually verify a sign-up flow a few times a day.",
							},
							{
								title: "@mailinator.com is blocked everywhere",
								body: "Many production and staging environments copy blocklist configs from their production sign-up form. If your staging environment routes real email, Mailinator domains may be rejected at the SMTP level or filtered before delivery.",
							},
						].map((item) => (
							<div
								key={item.title}
								className="rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<h3 className="mb-2 text-sm font-semibold text-foreground">
									{item.title}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground">
									{item.body}
								</p>
							</div>
						))}
					</div>
					<p className="mt-6 text-sm text-muted-foreground">
						Need Mailinator's API for automated testing?{" "}
						<a
							href="/alternatives/mailinator"
							className="text-primary underline-offset-4 hover:underline"
						>
							See the full Mailinator comparison
						</a>{" "}
						— including when Mailinator is the right tool and ineedatest.email
						is not.
					</p>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about using ineedatest.email for testing"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + related links */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Start testing — no account needed
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Spin up a test inbox in under 30 seconds.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							More on ineedatest.email:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Other ways to use disposable email
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/for/sign-up"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For sign-ups
							</a>
							<a
								href="/for/testing"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For OTP codes
							</a>
							<a
								href="/for/no-registration"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								No registration required
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
